/**
 * KAF Minutes on a general meeting (Aziz, 2026-09-28; rebuilt 2026-10-02: "it should be
 * meeting minutes with details, transferred into actions"). The minutes in full: summary,
 * the topics with their points, decisions, open questions and every action point with
 * its details, owner, side and the moment it was said; how much of the call the
 * transcript caught; one button for the suggested outcomes and one to redraft.
 * Suggestions are only ever that; the organiser files.
 */
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle2, HelpCircle, ListChecks, NotebookPen, RefreshCw, Wand2 } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import { redraftMinutes, setMeetingItem, type Meeting, type Outcome } from '@/lib/calls';
import { bidi, BIDI_CLS } from '@/lib/bidi';
import { cn } from '@/lib/cn';

const At = ({ at }: { at?: string | null }) => (at ? <span className="text-[10px] font-mono text-muted ml-1.5">{at}</span> : null);
const Unsure = ({ on }: { on?: boolean }) => (on ? <span className="text-[10px] text-amber-300 ml-1.5" title="KAF Minutes was unsure of the words here">unsure</span> : null);

export default function MinutesCard({ m, editable, onChange }: { m: Meeting; editable: boolean; onChange: (n?: Meeting) => void }) {
  const open = (m.items ?? []).filter((i) => !i.outcome && !i.filed_at && i.suggested_outcome);
  const useAll = useMutation({
    mutationFn: async () => {
      let last: Meeting | undefined;
      for (const it of open) {
        last = await setMeetingItem(m.id, it.id, { outcome: it.suggested_outcome as Outcome, clinic_note: it.clinic_note ?? '',
          staff_note: it.staff_note || it.suggested_note || '' });
      }
      return last;
    },
    onSuccess: (x) => { toast.success('Suggestions used. Check them, then file.'); onChange(x); },
    onError: () => toast.error('Some suggestions could not be used. Try again.'),
  });
  const redraft = useMutation({
    mutationFn: () => redraftMinutes(m.id),
    onSuccess: (x) => { toast.success('Drafting the minutes again. They appear here in about a minute.'); onChange(x); },
    onError: (e: unknown) => toast.error((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Could not redraft'),
  });
  if (m.minutes_state === 'pending') {
    return (
      <Card data-testid="minutes-card">
        <div className="flex items-center gap-2 text-sm text-muted"><RefreshCw size={14} className="animate-spin" /> KAF Minutes is drafting the minutes from the transcript.</div>
      </Card>
    );
  }
  if (!m.summary && m.minutes_state !== 'failed') return null;
  const doc = m.minutes_doc;
  const q = doc?.quality;
  const ours = (doc?.actions ?? []).filter((a) => a.side !== 'clinic');
  const theirs = (doc?.actions ?? []).filter((a) => a.side === 'clinic');
  const ActionList = ({ list }: { list: NonNullable<typeof doc>['actions'] }) => (
    <ol className="space-y-2.5 list-decimal pl-5">
      {list.map((a, i) => (
        <li key={i} className="text-sm text-muted-strong" data-testid="minutes-action">
          <div className="font-medium">{a.title}<Unsure on={a.unsure} /></div>
          {a.details && <div className={cn('text-xs text-muted mt-0.5 whitespace-pre-wrap', BIDI_CLS)} dir="auto">{bidi(a.details)}</div>}
          <div className="text-[11px] text-muted mt-0.5 flex flex-wrap gap-x-3">
            <span>Owner: <span className="text-muted-strong">{a.owner ?? a.owner_hint ?? 'to assign'}</span></span>
            <span>Due: <span className="text-muted-strong">{a.due_date ?? 'not said'}</span>{a.when_said ? ` ("${a.when_said}")` : ''}</span>
            {a.at && <span>Said at {a.at}</span>}
          </div>
          {a.quote && <div className={cn('text-[11px] text-muted italic mt-0.5', BIDI_CLS)} dir="auto">{bidi('«' + a.quote + '»')}</div>}
        </li>
      ))}
    </ol>
  );

  return (
    <Card data-testid="minutes-card">
      <div className="flex items-center gap-2 mb-2">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-wide text-muted"><NotebookPen size={12} /> Draft minutes</div>
        {editable && (
          <Button size="sm" variant="ghost" className="ml-auto" icon={<RefreshCw size={12} />} loading={redraft.isPending}
            onClick={() => redraft.mutate()} data-testid="minutes-redraft">Redraft</Button>
        )}
      </div>
      {q && q.level !== 'good' && q.note && (
        <div className={cn('flex gap-2 rounded-lg border px-3 py-2 mb-3 text-xs', q.level === 'poor' ? 'border-red-500/40 bg-red-500/10 text-red-200' : 'border-amber-500/40 bg-amber-500/10 text-amber-200')} data-testid="minutes-quality">
          <AlertTriangle size={14} className="shrink-0 mt-0.5" /> <span>{q.note}</span>
        </div>
      )}
      {m.minutes_state === 'failed' && !m.summary && <p className="text-sm text-amber-300">KAF Minutes could not draft this meeting. The transcript is there; record the outcomes yourself.</p>}
      {m.summary && <p className={`text-sm text-muted-strong whitespace-pre-wrap ${BIDI_CLS}`} dir="auto" data-testid="minutes-summary">{bidi(m.summary)}</p>}

      {!!doc?.topics?.length && (
        <section className="mt-4" data-testid="minutes-topics">
          <h4 className="text-[11px] uppercase tracking-wide text-muted mb-2">What was discussed</h4>
          <div className="space-y-3">
            {doc.topics.map((t, i) => (
              <div key={i}>
                <div className="text-sm font-medium text-muted-strong">{i + 1}. {t.title}<At at={t.at} /><Unsure on={t.unsure} /></div>
                <ul className="list-disc pl-5 mt-1 space-y-0.5">
                  {t.points.map((p, j) => <li key={j} className={cn('text-xs text-muted', BIDI_CLS)} dir="auto">{bidi(p)}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </section>
      )}
      {!!doc?.decisions?.length && (
        <section className="mt-4" data-testid="minutes-decisions">
          <h4 className="text-[11px] uppercase tracking-wide text-muted mb-2">Decisions</h4>
          <ul className="space-y-1">
            {doc.decisions.map((d, i) => (
              <li key={i} className="text-sm text-muted-strong flex gap-2"><CheckCircle2 size={13} className="text-emerald-400 shrink-0 mt-0.5" /><span>{d.text}<At at={d.at} /><Unsure on={d.unsure} /></span></li>
            ))}
          </ul>
        </section>
      )}
      {!!ours.length && (
        <section className="mt-4" data-testid="minutes-actions">
          <h4 className="text-[11px] uppercase tracking-wide text-muted mb-2 inline-flex items-center gap-1.5"><ListChecks size={12} /> Action points for KAF PRO ({ours.length})</h4>
          <ActionList list={ours} />
          <p className="text-[11px] text-muted mt-2">Each one is waiting below as a draft task with these details. Check the owner and date, then send.</p>
        </section>
      )}
      {!!theirs.length && (
        <section className="mt-4" data-testid="minutes-clinic-actions">
          <h4 className="text-[11px] uppercase tracking-wide text-muted mb-2">Action points for the clinic ({theirs.length})</h4>
          <ActionList list={theirs} />
        </section>
      )}
      {!!doc?.open_questions?.length && (
        <section className="mt-4" data-testid="minutes-questions">
          <h4 className="text-[11px] uppercase tracking-wide text-muted mb-2">Still open</h4>
          <ul className="space-y-1">
            {doc.open_questions.map((x, i) => (
              <li key={i} className="text-sm text-muted-strong flex gap-2"><HelpCircle size={13} className="text-sky-400 shrink-0 mt-0.5" /><span>{x.text}<At at={x.at} /></span></li>
            ))}
          </ul>
        </section>
      )}

      <p className="text-xs text-muted mt-4">Drafted by KAF Minutes from the transcript. It suggests; you decide. Nothing is filed or sent until you do it.</p>
      {editable && open.length > 0 && (
        <div className="mt-3">
          <Button size="sm" variant="secondary" icon={<Wand2 size={13} />} loading={useAll.isPending} onClick={() => useAll.mutate()} data-testid="use-all-suggestions">
            Use the {open.length} suggested outcome{open.length === 1 ? '' : 's'}
          </Button>
        </div>
      )}
    </Card>
  );
}
